import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  CollaboratorNote,
  CollaboratorNoteEligibleRecipients,
  CollaboratorNoteEligibleUser,
  CollaboratorNoteProfileMode,
  copyCollaboratorNote,
  createCollaboratorNote,
  deleteCollaboratorNote,
  fetchCollaboratorNoteAttachmentDownloadUrl,
  fetchNoteEligibleRecipients,
  listCollaboratorNotes,
  listEvaluationCycles,
  uploadCollaboratorNoteAttachment,
  EvaluationCycle,
} from '../../services/peoplePerformanceApi';

const NOTE_TYPE_OPTIONS = [
  { value: 'Observation', label: 'Observação / fato' },
  { value: 'Praise', label: 'Elogio' },
  { value: 'Warning', label: 'Advertência' },
  { value: 'Highlight', label: 'Destaque' },
];

const PROFILE_LABELS: Record<string, string> = {
  collaborator: 'Colaborador',
  manager: 'Gestor',
  hr: 'RH',
};

type RecipientDraft = {
  userId: string;
  selected: boolean;
  isWitness: boolean;
};

function noteTypeLabel(value: string): string {
  return NOTE_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function profileHint(mode: string, isCompanyContext: boolean): string {
  if (isCompanyContext) {
    return 'Contexto RH: você está anotando pela empresa, para qualquer colaborador.';
  }
  if (mode === 'manager') {
    return 'Como gestor, você pode criar anotações sobre si mesmo, seus pares e a equipe (ou apenas os selecionados).';
  }
  return 'Como colaborador, você pode criar notas sobre si mesmo ou sobre seu gestor imediato.';
}

export function NotesPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const aboutUserId = searchParams.get('aboutUserId') ?? '';

  const { access, canWriteModule } = usePermissions();
  const isHrAdmin = (access?.roleCodes ?? []).some((code) => code.toLowerCase() === 'admin');
  const isCompanyContext = location.pathname.startsWith('/people/notes') && isHrAdmin;
  const notesContext = isCompanyContext ? 'company' : 'personal';

  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('people.notes');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();

  const [items, setItems] = useState<CollaboratorNote[]>([]);
  const [eligible, setEligible] = useState<CollaboratorNoteEligibleRecipients | null>(null);
  const [cycles, setCycles] = useState<EvaluationCycle[]>([]);
  const [recipients, setRecipients] = useState<RecipientDraft[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [type, setType] = useState('Observation');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [deliverToInbox, setDeliverToInbox] = useState(false);
  const [sendByEmail, setSendByEmail] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const profileMode: CollaboratorNoteProfileMode | string = eligible?.profileMode ?? 'collaborator';

  const aboutUserName = useMemo(() => {
    if (!aboutUserId) return null;
    return eligible?.users.find((user) => user.id === aboutUserId)?.name ?? null;
  }, [aboutUserId, eligible]);

  const selectedCount = useMemo(
    () => recipients.filter((item) => item.selected).length,
    [recipients],
  );

  async function loadNotes(): Promise<void> {
    const result = await listCollaboratorNotes({
      pageSize: 100,
      aboutUserId: aboutUserId || undefined,
    });
    setItems(result.items);
  }

  async function loadComposerContext(): Promise<void> {
    const [eligibleResult, cyclesResult] = await Promise.all([
      fetchNoteEligibleRecipients(notesContext),
      listEvaluationCycles({ pageSize: 100 }).catch(() => ({ items: [] as EvaluationCycle[] })),
    ]);
    setEligible(eligibleResult);
    setCycles(cyclesResult.items);

    const preferAbout = aboutUserId && eligibleResult.users.some((user) => user.id === aboutUserId);
    setRecipients(
      eligibleResult.users.map((user: CollaboratorNoteEligibleUser) => ({
        userId: user.id,
        selected: preferAbout
          ? user.id === aboutUserId
          : eligibleResult.profileMode === 'collaborator' && eligibleResult.users.length === 1,
        isWitness: false,
      })),
    );

    if (preferAbout && canWrite) {
      expandForm();
    } else if (aboutUserId && !preferAbout) {
      toast.warn('Esta pessoa não está no seu escopo de anotações.');
    }
  }

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        await Promise.all([loadNotes(), loadComposerContext()]);
      } catch {
        toast.error('Falha ao carregar anotações.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesContext, aboutUserId]);

  function clearAboutFilter(): void {
    const next = new URLSearchParams(searchParams);
    next.delete('aboutUserId');
    setSearchParams(next, { replace: true });
  }

  function resetForm(): void {
    setType('Observation');
    setTitle('');
    setMessage('');
    setCycleId('');
    setDeliverToInbox(false);
    setSendByEmail(false);
    setSelectAll(false);
    setPendingFiles([]);
    setRecipients((current) =>
      current.map((item) => ({
        ...item,
        selected:
          aboutUserId && item.userId === aboutUserId
            ? true
            : eligible?.profileMode === 'collaborator' && (eligible?.users.length ?? 0) === 1,
        isWitness: false,
      })),
    );
  }

  function toggleRecipient(userId: string): void {
    setSelectAll(false);
    setRecipients((current) =>
      current.map((item) =>
        item.userId === userId ? { ...item, selected: !item.selected } : item,
      ),
    );
  }

  function toggleWitness(userId: string): void {
    setRecipients((current) =>
      current.map((item) =>
        item.userId === userId ? { ...item, isWitness: !item.isWitness } : item,
      ),
    );
  }

  function handleSelectAll(checked: boolean): void {
    setSelectAll(checked);
    setRecipients((current) => current.map((item) => ({ ...item, selected: checked })));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const selectedRecipients = recipients.filter((item) => item.selected);
    if (!selectAll && selectedRecipients.length === 0) {
      toast.error('Selecione ao menos um destinatário.');
      return;
    }

    if (pendingFiles.length > 10) {
      toast.error('Máximo de 10 anexos por nota (limite configurável na administração).');
      return;
    }

    setSaving(true);
    try {
      const created = await createCollaboratorNote({
        type,
        title,
        message,
        cycleId: cycleId || undefined,
        deliverToInbox,
        sendByEmail,
        context: notesContext,
        selectAllEligible: selectAll && !!eligible?.canSelectAll,
        recipients: selectedRecipients.map((item) => ({
          userId: item.userId,
          isWitness: item.isWitness,
        })),
      });

      for (const file of pendingFiles) {
        await uploadCollaboratorNoteAttachment(created.id, file);
      }

      const deliveryParts = [
        deliverToInbox ? 'enviada à caixa dos destinatários' : null,
        sendByEmail ? 'enviada por e-mail' : null,
      ].filter(Boolean);
      toast.success(
        deliveryParts.length > 0
          ? `Nota criada e ${deliveryParts.join(' e ')}.`
          : 'Nota criada (visível apenas para você).',
      );
      handleCreateSuccess(resetForm);
      await loadNotes();
    } catch {
      toast.error('Falha ao salvar nota.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCopy(note: CollaboratorNote): Promise<void> {
    try {
      await copyCollaboratorNote(note.id);
      toast.success('Cópia pessoal criada. Ela permanece mesmo se o autor excluir a nota.');
      await loadNotes();
    } catch {
      toast.error('Não foi possível criar a cópia.');
    }
  }

  async function handleDelete(note: CollaboratorNote): Promise<void> {
    const ok = await confirm({
      title: 'Excluir nota',
      message: note.viewerIsAuthor
        ? 'Excluir esta nota da sua visão? Destinatários com cópia pessoal continuarão vendo a mensagem.'
        : 'Remover esta nota da sua caixa?',
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await deleteCollaboratorNote(note.id);
      toast.success('Nota excluída.');
      await loadNotes();
    } catch {
      toast.error('Falha ao excluir nota.');
    }
  }

  async function handleDownload(noteId: string, attachmentId: string): Promise<void> {
    try {
      const result = await fetchCollaboratorNoteAttachmentDownloadUrl(noteId, attachmentId);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Falha ao obter o anexo.');
    }
  }

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of eligible?.users ?? []) {
      map.set(user.id, user.name);
    }
    return map;
  }, [eligible]);

  return (
    <PageShell>
      <PageHeader
        title={isCompanyContext ? 'Anotações (RH)' : 'Anotações'}
        description={
          isCompanyContext
            ? 'Registro corporativo de fatos, elogios e advertências em nome da empresa.'
            : profileMode === 'manager'
              ? 'Anote sobre você, seus pares e a equipe. Anotações da equipe também podem ser abertas a partir de Minha Equipe.'
              : 'Suas anotações como colaborador ou gestor — abaixo de Desempenho.'
        }
        actions={
          <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-700">
            {isCompanyContext ? 'Contexto empresa' : `Perfil: ${PROFILE_LABELS[profileMode] ?? profileMode}`}
          </span>
        }
      />

      {aboutUserId ? (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          <span>
            Filtrando anotações sobre{' '}
            <strong>{aboutUserName ?? 'colaborador selecionado'}</strong>.
          </span>
          <button type="button" className="erp-link text-sm" onClick={clearAboutFilter}>
            Limpar filtro
          </button>
        </div>
      ) : null}

      {canWrite ? (
        <CollapsibleFormSection
          title="Nova anotação"
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          createSummary="Clique para expandir e registrar uma nova anotação"
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form space-y-4 px-4 pb-4 pt-3">
            <p className="text-sm text-secondary-600">{profileHint(profileMode, isCompanyContext)}</p>

            <div className="erp-form-grid">
              <label className="erp-label">
                Tipo
                <select className="erp-input" value={type} onChange={(event) => setType(event.target.value)} required>
                  {NOTE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Ciclo de avaliação (opcional)
                <select className="erp-input" value={cycleId} onChange={(event) => setCycleId(event.target.value)}>
                  <option value="">Sem vínculo a ciclo</option>
                  {cycles.map((cycle) => (
                    <option key={cycle.id} value={cycle.id}>
                      {cycle.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="erp-label block">
              Título
              <input className="erp-input mt-1" value={title} onChange={(event) => setTitle(event.target.value)} required maxLength={200} />
            </label>

            <label className="erp-label block">
              Mensagem
              <textarea
                className="erp-input mt-1 min-h-[120px]"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                required
                maxLength={4000}
              />
            </label>

            <fieldset className="rounded-lg border border-secondary-200 p-3">
              <legend className="px-1 text-sm font-medium text-secondary-800">Destinatários</legend>
              {eligible?.canSelectAll ? (
                <label className="erp-label-inline mb-2">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(event) => handleSelectAll(event.target.checked)}
                  />
                  {isCompanyContext || profileMode === 'hr'
                    ? 'Todos os colaboradores'
                    : 'Todos os elegíveis'}
                </label>
              ) : null}
              <ul className="max-h-56 space-y-2 overflow-y-auto">
                {recipients.map((item) => {
                  const user = eligible?.users.find((entry) => entry.id === item.userId);
                  if (!user) return null;
                  return (
                    <li key={item.userId} className="flex flex-wrap items-center gap-3 text-sm">
                      <label className="erp-label-inline">
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={() => toggleRecipient(item.userId)}
                        />
                        <span className="font-medium text-secondary-900">{user.name}</span>
                        <span className="text-secondary-500">({user.email})</span>
                      </label>
                      {item.selected ? (
                        <label className="erp-label-inline text-xs text-secondary-600">
                          <input
                            type="checkbox"
                            checked={item.isWitness}
                            onChange={() => toggleWitness(item.userId)}
                          />
                          Testemunha (governança)
                        </label>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 text-xs text-secondary-500">
                {selectAll ? 'Todos os elegíveis serão incluídos.' : `${selectedCount} selecionado(s).`}
              </p>
            </fieldset>

            <fieldset className="rounded-lg border border-secondary-200 p-3">
              <legend className="px-1 text-sm font-medium text-secondary-800">Entrega</legend>
              <p className="mb-2 text-xs text-secondary-500">
                Sem as opções abaixo, a nota fica disponível apenas para você.
              </p>
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={deliverToInbox}
                  onChange={(event) => setDeliverToInbox(event.target.checked)}
                />
                Enviar para o(s) destinatário(s) (caixa de anotações)
              </label>
              <label className="erp-label-inline mt-2">
                <input
                  type="checkbox"
                  checked={sendByEmail}
                  onChange={(event) => setSendByEmail(event.target.checked)}
                />
                Enviar por e-mail
              </label>
            </fieldset>

            <label className="erp-label block">
              Anexos (PDF ou imagem, até 10 — limite configurável)
              <input
                className="erp-input mt-1"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,application/pdf,image/*"
                multiple
                onChange={(event) => setPendingFiles(Array.from(event.target.files ?? []).slice(0, 10))}
              />
              {pendingFiles.length > 0 ? (
                <ul className="mt-2 text-xs text-secondary-600">
                  {pendingFiles.map((file) => (
                    <li key={`${file.name}-${file.size}`}>{file.name}</li>
                  ))}
                </ul>
              ) : null}
            </label>

            <FormActionBar onCancel={() => collapseForm(resetForm)} className="erp-btn-actions">
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Criar anotação'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : items.length === 0 ? (
        <p className="text-secondary-500">Nenhuma anotação visível para você.</p>
      ) : (
        <div className="space-y-3">
          {items.map((note) => {
            const expanded = expandedId === note.id;
            return (
              <article key={note.id} className="rounded-xl border border-secondary-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-secondary-500">
                      <span className="rounded bg-primary-50 px-2 py-0.5 font-medium text-primary-700">
                        {noteTypeLabel(note.type)}
                      </span>
                      <span>{new Date(note.createdAtUtc).toLocaleString('pt-BR')}</span>
                      <span>por {note.authorName || '—'}</span>
                      {note.viewerIsAuthor ? <span className="text-secondary-400">· sua nota</span> : null}
                      {note.viewerHasPersonalCopy ? <span className="text-secondary-400">· cópia pessoal</span> : null}
                    </div>
                    <h3 className="mt-1 font-semibold text-secondary-900">{note.title}</h3>
                    <p className="mt-1 text-sm text-secondary-600">
                      Destinatários:{' '}
                      {note.recipients
                        .map((recipient) => {
                          const name = recipient.recipientName || userNameById.get(recipient.recipientUserId) || '—';
                          return recipient.isWitness ? `${name} (testemunha)` : name;
                        })
                        .join(', ') || '—'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="erp-btn-secondary text-sm"
                      onClick={() => setExpandedId(expanded ? null : note.id)}
                    >
                      {expanded ? 'Ocultar' : 'Ver'}
                    </button>
                    {note.canCopy ? (
                      <button type="button" className="erp-btn-secondary text-sm" onClick={() => void handleCopy(note)}>
                        Fazer cópia
                      </button>
                    ) : null}
                    {note.canDelete ? (
                      <DeleteIconButton label="Excluir nota" onClick={() => void handleDelete(note)} />
                    ) : null}
                  </div>
                </div>

                {expanded ? (
                  <div className="mt-3 space-y-3 border-t border-secondary-100 pt-3">
                    <p className="whitespace-pre-wrap text-sm text-secondary-800">{note.message}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-secondary-500">
                      {note.deliverToInbox ? <span>Entregue na caixa</span> : <span>Privada do autor</span>}
                      {note.sendByEmail ? <span>· E-mail solicitado</span> : null}
                    </div>
                    {note.attachments.length > 0 ? (
                      <ul className="space-y-1 text-sm">
                        {note.attachments.map((attachment) => (
                          <li key={attachment.id}>
                            <button
                              type="button"
                              className="erp-link"
                              onClick={() => void handleDownload(note.id, attachment.id)}
                            >
                              {attachment.fileName}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-secondary-500">Sem anexos.</p>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
